'use client'

import type { Cruz } from '@cruz-com/lookup'
import { Popover, PopoverBackdrop, PopoverButton, PopoverPanel } from '@headlessui/react'
import { deleteCookie } from 'cookies-next/client'
import dayjs from 'dayjs'
import _ from 'lodash'
import Link from 'next/link'
import { redirect, usePathname } from 'next/navigation'
import type React from 'react'
import { memo, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { HiChevronDown } from 'react-icons/hi'
import { myCruzApi } from '@/api'
import { Avatar, CruzIcon, Divider, LinkButton, MenuLink } from '@/components'
import { pages } from '@/settings'
import { useGlobalStore } from '@/store/provider'
import { ToggleBodyScroll } from './toggleBodyScroll'

type MenuProps = {
  name: string
  icon?: React.ReactNode
  href: string
  count?: number
  onClick: () => void
}

// Components
const MyMenuItem = memo(({ name, href, count, icon, onClick }: MenuProps) => (
  <Link hrefLang='en' key={href} href={href} className='menu-item' onClick={onClick}>
    <div className='flex items-center gap-2'>
      {icon}
      {name}
    </div>
    {!_.isUndefined(count) && (
      <>
        <span className='sr-only'>, count: </span>
        <span className='nav-count'>{count}</span>
      </>
    )}
  </Link>
))

const UserInfo = memo(({ user }: { user: Cruz.UserDisplay }) => (
  <address className='flex flex-col space-y-1 truncate px-6 pt-4 pb-2 text-center not-italic dark:text-zinc-200'>
    <div className='truncate text-base/6 font-semibold'>
      {_.isEmpty(user?.first_name) ? user?.email : `${user?.first_name} ${user?.last_name}`}
    </div>
    {!_.isEmpty(user?.location) && (
      <div className='truncate text-sm font-light sm:text-xs'>
        {user?.location?.city}, {user?.location?.state_abbr} {user?.location?.zip_code}
      </div>
    )}
    <div className='truncate text-sm font-light sm:text-xs'>Joined {dayjs(user?.date_create).fromNow()}</div>
  </address>
))

const SignOutButton = memo(({ onClick }: { onClick: () => void }) => (
  <button type='button' className='menu-item w-full' onClick={onClick}>
    <span className='flex w-full items-center gap-2'>
      <CruzIcon.LogOut className='mr-0.5' />
      Sign Out
    </span>
  </button>
))

export const HeaderMyMenu = memo(() => {
  const pathname = usePathname()
  const { user, setUser, refreshMyProfile, setRefreshMyProfile, setToast } = useGlobalStore((state) => state)

  const getMyProfile = useCallback(async () => {
    const d = (await myCruzApi.getProfile())?.data
    if (d) {
      setUser(d, null)
    }
  }, [setUser])

  const signOut = useCallback(() => {
    setUser({}, null)
    deleteCookie('access_token')
    deleteCookie('refresh_token')
    if (_.includes(pathname, '/my')) {
      redirect(pages.home.href)
    } else {
      setToast({ type: 'success', message: 'Successfully signed out' })
    }
  }, [pathname])

  useEffect(() => {
    if (refreshMyProfile) {
      setRefreshMyProfile(false)
      getMyProfile()
    }
  }, [refreshMyProfile])

  if (_.isEmpty(user)) {
    return (
      <>
        <LinkButton variant='secondary' href={pages.signIn.href} label={pages.signIn.name} />
        <MenuLink href={pages.joinCruz.href} className='truncate'>
          Join Cruz<sup className='text-[7px]'>TM</sup>
        </MenuLink>
      </>
    )
  }

  const { statistic, first_name, last_name, photo_url } = user || {}

  return (
    <Popover as='div' className='relative'>
      {({ open, close }) => (
        <>
          <ToggleBodyScroll open={open} />
          {open &&
            typeof document !== 'undefined' &&
            createPortal(<PopoverBackdrop className='dialog-backdrop z-10!' onClick={close} />, document.body)}
          <PopoverButton className='menu-button font-medium dark:font-medium' aria-label='User Profile Menu'>
            <Avatar className='size-12 shadow-inner md:size-10' photo_url={photo_url} name={`${first_name} ${last_name}`} />
            <HiChevronDown className='-mr-1 ml-1 size-4' aria-hidden='true' />
          </PopoverButton>
          <PopoverPanel
            as='nav'
            aria-label='User Quick Links'
            anchor='bottom end'
            transition={true}
            modal={false}
            className='menu-items rounded-l-md pt-2 pb-4'
          >
            <UserInfo user={user} />
            <Divider className='py-1' />
            <MyMenuItem {...pages.myCruz} onClick={close} />
            <Divider className='py-1' />
            <MyMenuItem {...pages.mySavedVehicle} count={statistic?.saved_vehicle || 0} onClick={close} />
            <MyMenuItem {...pages.mySavedSearch} count={statistic?.saved_search || 0} onClick={close} />
            <MyMenuItem {...pages.myAskCruzAi} count={statistic?.ai_search_count || 0} onClick={close} />
            <MyMenuItem {...pages.myRecentlyViewed} count={statistic?.recently_viewed_count || 0} onClick={close} />
            <Divider className='py-1' />
            <MyMenuItem {...pages.myInquiries} count={statistic?.inquiry_count || 0} onClick={close} />
            <Divider className='py-1' />
            <MyMenuItem {...pages.myVehicles} count={statistic?.vehicle?.all || 0} onClick={close} />
            <MyMenuItem {...pages.postVehicle} onClick={close} icon={<CruzIcon.Plus className='mr-0.5' />} />
            <Divider className='py-1' />
            <MyMenuItem {...pages.mySpotted} count={statistic?.spotted_count || 0} onClick={close} />
            <MyMenuItem {...pages.postSpotted} onClick={close} icon={<CruzIcon.Plus className='mr-0.5' />} />
            <Divider className='py-1' />
            <MyMenuItem {...pages.myNotificationSettings} onClick={close} icon={<CruzIcon.NotificationOn className='mr-0.5' />} />
            <Divider className='py-1' />
            <MyMenuItem {...pages.myProfile} onClick={close} />
            <MyMenuItem {...pages.myPassword} onClick={close} />
            <SignOutButton
              onClick={() => {
                close()
                signOut()
              }}
            />
          </PopoverPanel>
        </>
      )}
    </Popover>
  )
})
